import { User } from '.';
import debounce from 'lodash-es/debounce';
import { action, computed, observable, reaction } from 'mobx';
import { UserType } from '../../../common/interfaces/user';
import { app, browserHistory } from './../App';

// Interfaces
import {
  IProvider,
  IProviderInfo,
  IProviderFindApiQuery,
  CreateLeadModalStatus,
  ICreateNewLead,
  CreateNewLeadStatus,
  IProvidersToDisplay,
  IProviderTypeOrClass,
  IProviderTypesOrClassesResponse,
  IProviderNotesApiQuery,
  INotesToDisplay as INoteToDisplay,
  ILeadNoteSearched,
  ILeadNotesSearchInfo,
  EDashboardView,
} from 'common/interfaces/provider';

// Enumerables
export enum LeadEditableProperties {
  dba = 'dba',
  legalBusName = 'legalBusName',
  providerId = 'providerId',
  plAddress1 = 'plAddress1',
  plAddress2 = 'plAddress2',
  plCity = 'plCity',
  plState = 'plState',
  plZipcode = 'plZipcode',
  plPhone = 'plPhone',
  plExt = 'plExt',
  plFax = 'plFax',
  plEmail = 'plEmail',
}

export enum CreateLeadProperties {
  providerId = 'providerId',
  legalBusName = 'legalBusName',
  dba = 'dba',
}

export enum LeadsSearchFilter {
  All = '',
  Name = 'name',
  Phone = 'phone',
  State = 'state',
  Area = 'areacode',
  City = 'city',
  Notes = 'notes',
}

// Store
export class Leads {
  constructor(private user: User) {
    this.fetchLeadsDebounced = debounce(this.fetchLeadsDebounced, 300);
    this.fetchNotesDebounced = debounce(this.fetchNotesDebounced, 300);
  }

  // Reset search and load initial bunch of leads when user changes
  private reactToLoggedUserChange = reaction(
    () => this.user.id,
    (id: number) => {
      if (id !== null) {
        this.leadsToDisplayByQuery.clear();
        this.allLeads = [];
        this.leadsSearch = '';
        this.fetchLeads();
        this.setLeadsDashboardView();
      }
    }
  );

  // Static values
  public LEADS_OR_NOTES_PER_PAGE_LIMIT: number = 99;

  // Limit and offset / skip
  @observable public leadsFetchLimit: number = this
    .LEADS_OR_NOTES_PER_PAGE_LIMIT;
  @observable public leadsFetchOffset: number = 0;

  // Leads dashboard view
  @observable public leadsDashboardView: EDashboardView = EDashboardView.Grid;

  // All leads
  @observable public modalStatus: CreateLeadModalStatus =
    CreateLeadModalStatus.Closed;

  @observable public allLeads: IProvider[] = [];

  @computed public get allLeadsIds(): number[] {
    return this.allLeads.map((lead) => lead.databaseId);
  }

  public leadsToDisplayByQuery = observable.map<IProvidersToDisplay>();

  @computed public get leadsToDisplay(): IProvidersToDisplay {
    const key = this.leadsQueryKey;

    if (!key || !this.leadsToDisplayByQuery.has(key)) {
      return {
        totalLeads: 0,
        lastFetchTimestamp: 0,
        firstFetch: true,
        isLoading: true,
        leadsIds: [],
      };
    }

    return this.leadsToDisplayByQuery.get(key);
  }

  @computed public get leadsToDisplayIds(): number[] {
    return this.leadsToDisplay.leadsIds;
  }

  @computed public get leadsToDisplayCount(): number {
    return this.leadsToDisplayIds.length;
  }

  @computed public get leadsToDisplayDetails(): IProvider[] {
    return this.leadsToDisplayIds.reduce((leads: IProvider[], leadId) => {
      const index = this.allLeadsIds.indexOf(leadId);
      if (index !== -1) {
        leads.push(this.allLeads[index]);
      }

      return leads;
    }, []);
  }

  @computed public get notesToDisplayIds(): number[] {
    return this.notesToDisplay.notesIds;
  }

  @computed public get notesToDisplayCount(): number {
    return this.notesToDisplayIds.length;
  }

  @computed public get notesToDisplayDetails(): ILeadNoteSearched[] {
    return this.notesToDisplayIds.reduce(
      (notes: ILeadNoteSearched[], noteId) => {
        const index = this.allNotesIds.indexOf(noteId);
        if (index !== -1) {
          notes.push(this.allNotes[index]);
        }

        return notes;
      },
      []
    );
  }

  // Providers search
  @observable public loadedDueToSearch: number = -1;
  @observable public leadsSearch: string = '';
  @observable public leadsSearchFilter: LeadsSearchFilter =
    LeadsSearchFilter.All;
  @observable public leadsFilterByActivityTypeId: number | null = null;
  @observable public leadsFilterByActivityDate: {
    from: string | null;
    to: string | null;
    noActivity: boolean;
  } | null = {
    from: null,
    to: null,
    noActivity: false,
  };
  @observable public lastLeadId: number = 0;
  @observable public lastNoteId: number = 0;

  // Notes search
  @observable public notesFetchLimit: number = this
    .LEADS_OR_NOTES_PER_PAGE_LIMIT;
  @observable public notesFetchOffset: number = 0;
  @observable public notesSearchQuery: string = '';

  @observable public allNotes: ILeadNoteSearched[] = [];

  @computed public get allNotesIds(): number[] {
    return this.allNotes.map((note) => note.id);
  }

  public notesToDisplayByQuery = observable.map<INoteToDisplay>();

  @computed public get notesToDisplay(): INoteToDisplay {
    const key = this.notesQueryKey;

    if (!key || !this.notesToDisplayByQuery.has(key)) {
      return {
        totalNotes: 0,
        lastFetchTimestamp: 0,
        firstFetch: true,
        isLoading: true,
        notesIds: [],
      };
    }

    return this.notesToDisplayByQuery.get(key);
  }

  // Provider types and dispenser classes
  @observable public providerTypes: {
    lastFetchTimestamp: number;
    list: IProviderTypeOrClass[];
  } = {
    lastFetchTimestamp: 0,
    list: [],
  };
  @observable public providerDispenserClasses: {
    lastFetchTimestamp: number;
    list: IProviderTypeOrClass[];
  } = {
    lastFetchTimestamp: 0,
    list: [],
  };

  @computed public get leadsInfoFetchApiQuery(): IProviderFindApiQuery {
    let query: IProviderFindApiQuery = {
      $limit: this.leadsFetchLimit,
      $skip: this.leadsFetchOffset,
    };

    if (this.leadsSearch.trim().length > 0) {
      query = {
        ...query,
        search: this.leadsSearch,
      };
    }

    if (
      this.leadsSearchFilter !== LeadsSearchFilter.All &&
      this.leadsSearchFilter !== LeadsSearchFilter.Notes
    ) {
      query = {
        ...query,
        searchType: this.leadsSearchFilter,
      };
    }

    if (this.leadsFilterByActivityTypeId !== null) {
      query = {
        ...query,
        lastCompletedActivityTypeId: this.leadsFilterByActivityTypeId,
      };
    }

    if (
      this.leadsFilterByActivityDate !== null &&
      this.leadsFilterByActivityDate.from !== null
    ) {
      query = {
        ...query,
        fromDate: this.leadsFilterByActivityDate.from,
      };
    }

    if (
      this.leadsFilterByActivityDate !== null &&
      this.leadsFilterByActivityDate.to !== null
    ) {
      query = {
        ...query,
        toDate: this.leadsFilterByActivityDate.to,
      };
    }

    if (
      this.leadsFilterByActivityDate !== null &&
      this.leadsFilterByActivityDate.noActivity === true
    ) {
      query = {
        ...query,
        noActivity: true,
      };
    }

    return query;
  }

  @computed public get leadsQueryKey(): string {
    const query =
      '?limit=' +
      this.leadsInfoFetchApiQuery.$limit +
      '&offset=' +
      this.leadsInfoFetchApiQuery.$skip +
      '&search=' +
      (this.leadsInfoFetchApiQuery.search
        ? this.leadsInfoFetchApiQuery.search
        : '') +
      '&searchType=' +
      (this.leadsInfoFetchApiQuery.searchType || '') +
      '&lastActivityTypeId=' +
      (this.leadsInfoFetchApiQuery.lastCompletedActivityTypeId
        ? this.leadsInfoFetchApiQuery.lastCompletedActivityTypeId
        : '') +
      '&noActivity=' +
      (this.leadsInfoFetchApiQuery.noActivity ? '1' : '0') +
      '&fromDate=' +
      (this.leadsInfoFetchApiQuery.fromDate
        ? this.leadsInfoFetchApiQuery.fromDate
        : '') +
      '&toDate=' +
      (this.leadsInfoFetchApiQuery.toDate
        ? this.leadsInfoFetchApiQuery.toDate
        : '');

    return query;
  }

  @computed public get notesFetchApiQuery(): IProviderNotesApiQuery {
    let query: IProviderNotesApiQuery = {
      $limit: this.notesFetchLimit,
      $skip: this.notesFetchOffset,
      search: '',
    };

    if (this.leadsSearchFilter === LeadsSearchFilter.Notes) {
      query = {
        ...query,
        search: this.notesSearchQuery,
      };
    }

    return query;
  }

  @computed public get notesQueryKey(): string {
    return (
      '?limit=' +
      this.notesFetchApiQuery.$limit +
      '&offset=' +
      this.notesFetchApiQuery.$skip +
      '&search=' +
      (this.notesFetchApiQuery.search || '')
    );
  }

  @observable public newLead: ICreateNewLead = {
    status: CreateNewLeadStatus.None,
    providerId: '',
    legalBusName: '',
    dba: '',
  };

  private reactToSearchQueryChange = reaction(
    () => this.leadsSearch,
    (query: string) => {
      this.leadsFetchOffset = 0;
      this.fetchLeadsDebounced();
    }
  );

  private reactToNotesSearchQueryChange = reaction(
    () => this.notesSearchQuery,
    (query: string) => {
      this.notesFetchOffset = 0;
      this.fetchNotesDebounced();
    }
  );

  private reactToSearchFilterChange = reaction(
    () => this.leadsSearchFilter,
    (filter: LeadsSearchFilter) => {
      this.leadsFetchOffset = 0;
      this.notesFetchOffset = 0;

      if (filter === LeadsSearchFilter.Notes) {
        this.fetchNotes();
      } else {
        this.fetchLeads();
      }
    }
  );

  private reactToSearchFilterByActivityTypeChange = reaction(
    () => this.leadsFilterByActivityTypeId,
    (id: number) => {
      this.leadsFetchOffset = 0;
      this.fetchLeadsDebounced();
    }
  );

  private reactToSearchFilterByActivityDateChange = reaction(
    () => this.leadsFilterByActivityDate,
    (date: { from: string; to: string }) => {
      this.leadsFetchOffset = 0;
      this.fetchLeadsDebounced();
    }
  );

  public fetchLeadsDebounced = () => {
    this.fetchLeads();
  };

  public fetchNotesDebounced = () => {
    this.fetchNotes();
  };

  // Fetch all leads based on search query
  @action public fetchLeads = async (): Promise<IProvider[]> => {
    const key = this.leadsQueryKey.slice();

    try {
      // Create query key and indicate loading
      if (!this.leadsToDisplayByQuery.has(key)) {
        this.leadsToDisplayByQuery.set(key, {
          lastFetchTimestamp: 0,
          firstFetch: true,
          isLoading: true,
          leadsIds: [],
          totalLeads: 0,
        });
      } else {
        const data = this.leadsToDisplayByQuery.get(key);
        if (data.lastFetchTimestamp > Date.now() - 60 * 1000 * 5) {
          return this.leadsToDisplayDetails;
        }

        this.leadsToDisplayByQuery.set(key, {
          ...data,
          isLoading: true,
        });
      }

      // Get leads matching filters visible to the user
      const leadsService: any = app.service('provider');
      const leadsInfo: IProviderInfo = await leadsService.find({
        query: this.leadsInfoFetchApiQuery,
      });

      // Update all leads state
      leadsInfo.data.forEach((lead) => {
        const index = this.allLeadsIds.indexOf(lead.databaseId);
        if (index !== -1) {
          this.allLeads[index] = lead;
        } else {
          this.allLeads.push(lead);
        }
      });

      // Update state
      this.leadsToDisplayByQuery.set(key, {
        lastFetchTimestamp: Date.now(),
        isLoading: false,
        firstFetch: false,
        totalLeads: leadsInfo.total,
        leadsIds: leadsInfo.data.map((lead) => lead.databaseId),
      });

      // Return leads data
      return leadsInfo.data;
    } catch (err) {
      if (this.leadsToDisplayByQuery.has(key)) {
        this.leadsToDisplayByQuery.set(key, {
          ...this.leadsToDisplayByQuery.get(key),
          isLoading: false,
        });
      }
      console.error(err);
      return err;
    }
  };

  // Search across all notes
  @action public fetchNotes = async (): Promise<ILeadNoteSearched[]> => {
    const key = this.notesQueryKey.slice();

    try {
      // Create notes key and indicate loading
      if (!this.notesToDisplayByQuery.has(key)) {
        this.notesToDisplayByQuery.set(key, {
          lastFetchTimestamp: 0,
          firstFetch: true,
          isLoading: true,
          notesIds: [],
          totalNotes: 0,
        });
      } else {
        const data = this.notesToDisplayByQuery.get(key);
        if (data.lastFetchTimestamp > Date.now() - 60 * 1000 * 5) {
          return this.notesToDisplayDetails;
        }

        this.notesToDisplayByQuery.set(key, {
          ...data,
          isLoading: true,
        });
      }

      // Get notes matching filters visible to the user
      const notesSearchService = app.service('comment-search');
      const notesSearchResults: ILeadNotesSearchInfo = await notesSearchService.find(
        {
          query: this.notesFetchApiQuery,
        }
      );

      // Update all leads state
      notesSearchResults.data.forEach((note) => {
        const index = this.allNotesIds.indexOf(note.id);
        if (index !== -1) {
          this.allNotes[index] = note;
        } else {
          this.allNotes.push(note);
        }
      });

      // Update state
      this.notesToDisplayByQuery.set(key, {
        isLoading: false,
        firstFetch: false,
        lastFetchTimestamp: Date.now(),
        totalNotes: notesSearchResults.total,
        notesIds: notesSearchResults.data.map((note) => note.id),
      });

      // Return leads data
      return notesSearchResults.data;
    } catch (err) {
      if (this.notesToDisplayByQuery.has(key)) {
        this.notesToDisplayByQuery.set(key, {
          ...this.notesToDisplayByQuery.get(key),
          isLoading: false,
        });
      }

      console.error(err);
      return err;
    }
  };

  // Load more leads as user scrolls
  @action public createNewLead = async (): Promise<any> => {
    const errorProviderIdRequired = 'Provider ID is required';
    const errorLegalBusNameRequired = 'Legal bussiness name is required';

    try {
      this.newLead.status = CreateNewLeadStatus.Saving;

      const { providerId, legalBusName, dba } = this.newLead;

      if (providerId.trim().length <= 0) {
        this.newLead.status = CreateNewLeadStatus.ProviderIdRequired;
        throw new Error(errorProviderIdRequired);
      }

      if (legalBusName.trim().length <= 0) {
        this.newLead.status = CreateNewLeadStatus.LegalBusNameRequired;
        throw new Error(errorLegalBusNameRequired);
      }

      const createLead: any = app.service('provider');
      const newLead: IProvider = await createLead.create({
        providerId: providerId.trim(),
        legalBusName: legalBusName.trim(),
        dba: dba.trim().length > 0 ? dba.trim() : null,
        slpcode:
          this.user.userType === UserType.SalesRep && this.user.slpcode !== null
            ? this.user.slpcode
            : null,
        createdManually: true,
      });

      this.newLead.status = CreateNewLeadStatus.Success;

      setTimeout(() => {
        this.modalStatus = CreateLeadModalStatus.Closed;
      }, 128);

      setTimeout(() => {
        this.newLead.providerId = '';
        this.newLead.legalBusName = '';
        this.newLead.dba = '';
        this.newLead.status = CreateNewLeadStatus.None;

        // Redirect to lead only if user will have permissions to access newly created lead
        if (
          this.user.userType !== UserType.SalesManager ||
          this.user.slpcode !== null
        ) {
          browserHistory.push('/sales/lead/' + newLead.databaseId);
        }
      }, 512);

      return true;
    } catch (err) {
      if (this.newLead.status === CreateNewLeadStatus.Saving) {
        this.newLead.status = CreateNewLeadStatus.ServerSideError;
      }

      console.error(err);
      return err;
    }
  };

  @action
  public changeLeadsPage = (page: number) => {
    this.leadsFetchOffset =
      page * this.LEADS_OR_NOTES_PER_PAGE_LIMIT -
      this.LEADS_OR_NOTES_PER_PAGE_LIMIT;

    this.fetchLeads();
  };

  @action public changeNotesPage = (page: number) => {
    this.notesFetchOffset =
      page * this.LEADS_OR_NOTES_PER_PAGE_LIMIT -
      this.LEADS_OR_NOTES_PER_PAGE_LIMIT;
    this.fetchNotes();
  };

  @action
  public fetchProviderTypes = async (): Promise<boolean> => {
    try {
      const now = Date.now();
      if (this.providerTypes.lastFetchTimestamp > now - 1000 * 60 * 30) {
        return true;
      }

      const service = await app.service('provider-type');
      const response = (await service.find({
        query: {
          $limit: 999999,
        },
      })) as IProviderTypesOrClassesResponse;

      this.providerTypes.lastFetchTimestamp = now;
      this.providerTypes.list = response.data;

      return true;
    } catch (error) {
      setTimeout(() => {
        this.fetchProviderTypes();
      }, 512);

      return false;
    }
  };

  @action
  public fetchDispenserClasses = async (): Promise<boolean> => {
    try {
      const now = Date.now();
      if (
        this.providerDispenserClasses.lastFetchTimestamp >
        now - 1000 * 60 * 30
      ) {
        return true;
      }

      const service = await app.service('provider-dispenser');
      const response = (await service.find({
        query: {
          $limit: 999999,
        },
      })) as IProviderTypesOrClassesResponse;

      this.providerDispenserClasses.lastFetchTimestamp = now;
      this.providerDispenserClasses.list = response.data;

      return true;
    } catch (error) {
      setTimeout(() => {
        this.fetchDispenserClasses();
      }, 512);

      return false;
    }
  };

  public returnProviderTypeOrDispenserClass = (
    code: string | null,
    select?: 'type' | 'dispenser'
  ): IProviderTypeOrClass | null => {
    if (typeof select === 'undefined' || code === null) {
      return null;
    }

    let index = -1;

    if (select === 'dispenser') {
      this.providerDispenserClasses.list.some(
        (dispenserClass, dispenserClassIndex) => {
          if (dispenserClass.code === code) {
            index = dispenserClassIndex;
            return true;
          }

          return false;
        }
      );
    } else if (select === 'type') {
      this.providerTypes.list.some((type, typeIndex) => {
        if (type.code === code) {
          index = typeIndex;
          return true;
        }

        return false;
      });
    }

    if (index === -1) {
      return null;
    }

    return select === 'dispenser'
      ? this.providerDispenserClasses.list[index]
      : select === 'type'
      ? this.providerTypes.list[index]
      : null;
  };

  @action
  public setLeadsDashboardView = (view?: EDashboardView) => {
    const viewKey = 'LeadsDashboardView';

    if (view) {
      view = this.verifyLeadsDashboardViewAccuracy(view);
    } else {
      let local =
        localStorage.getItem('LeadsDashboardView') || EDashboardView.Grid;
      local = this.verifyLeadsDashboardViewAccuracy(local as EDashboardView);
      view = local as EDashboardView;
    }

    localStorage.setItem(viewKey, view);
    this.leadsDashboardView = view;
  };

  private verifyLeadsDashboardViewAccuracy = (
    view: EDashboardView
  ): EDashboardView => {
    let isAccurate = false;
    Object.keys(EDashboardView).some((key) => {
      if (EDashboardView[key] === view) {
        isAccurate = true;
        return true;
      }

      return false;
    });

    if (!isAccurate) {
      view = EDashboardView.Grid;
    }

    return view;
  };
}



// WEBPACK FOOTER //
// ./node_modules/tslint-loader??ref--0!./app/web/src/stores/Leads.ts